import { InsightFeaturedArticle, InsightNews } from '@orbis-systems/orbis-api-clients/insight';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { MiddlewareInjectToken } from '../constants/tokens';
import { type Apis, type MiddlewareConfig } from '../types';
import { HoopsAiArticle, HoopsAiArticleWithExtraInfo } from '../types/News.type';

@injectable()
export class NewsApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
    ) {
        super(apis, config);
    }

    async getInsightNewsFiltered({
        symbols = [],
        endDate,
        paging,
        startDate,
        channels = [],
        tags = [],
    }: {
        symbols?: string[];
        endDate?: Date;
        paging?: {
            limit: number;
            offset: number;
        };
        startDate?: Date;
        channels?: string[];
        tags?: string[];
    }): Promise<InsightNews[]> {
        const response = await this.apis.insight.news.newsFiltered({
            end_date: endDate?.toISOString(),
            paging: paging ?? {
                limit: 10,
                offset: 0,
            },
            start_date: startDate?.toISOString(),
            channels,
            symbols: symbols.filter((symbol) => symbol),
            tags,
        });

        return response.data.news;
    }

    async getInsightNewsArticleById(id: string): Promise<InsightNews> {
        const response = await this.apis.insight.news.newsById({ id });
        return response.data;
    }

    async getInsightNewsTags(): Promise<string[]> {
        const response = await this.apis.insight.news.newsAvailableTags({});
        return response.data;
    }

    async getInsightNewsChannels(symbol?: string): Promise<string[]> {
        const response = await this.apis.insight.news.newsAvailableChannels({ symbol });
        return response.data;
    }

    async getInsightFeaturedArticles(): Promise<InsightFeaturedArticle[]> {
        const { data } = await this.apis.insight.news.getActiveFeaturedArticles();

        return data.featured_articles;
    }

    async getHoopsAiNews(symbols: string[]): Promise<HoopsAiArticleWithExtraInfo[]> {
        const symbolsList = symbols.join(',');
        const response = await this.apis.middleware.getApiClient().get<{
            result: (Record<string, HoopsAiArticle> | HoopsAiArticle) & { missing_symbol_list?: string[] };
        }>(`v1/hoopsai/v1/resources/finance/daily/${symbolsList}`, {
            secure: true,
            params: {
                images_enabled: true,
                separate_header: true,
                doc_group: 'Stocks_Daily',
            },
        });

        const result = response.data.result;
        delete result['missing_symbol_list'];

        return symbols.length === 1
            ? [{ ...(result as HoopsAiArticle), id: symbols[0]! }]
            : Object.entries(result).map(([id, article]) => ({
                  ...article,
                  id, //We need to use the symbol as an identifier
              }));
    }
}
